import { useState } from "react";
import { getAfiliadoEstadoMeta } from "@/lib/affiliateEstados";
import { formatFechaHora } from "@/lib/format";
import { DetailSection, EmptyLine } from "./AfiliadoDetailBits";
import type { AfiliadoHistorial, AfiliadoNota } from "@/types";

interface AfiliadoHistorialProps {
  historial: AfiliadoHistorial[];
  notas: AfiliadoNota[];
  addingNote: boolean;
  onAddNote: (nota: string) => Promise<boolean>;
}

export default function AfiliadoHistorial({
  historial,
  notas,
  addingNote,
  onAddNote,
}: AfiliadoHistorialProps) {
  const [draft, setDraft] = useState("");

  const handleAdd = async () => {
    const value = draft.trim();
    if (!value) return;
    const ok = await onAddNote(value);
    if (ok) setDraft("");
  };

  return (
    <div className="space-y-5">
      <DetailSection title="Notas administrativas" icon="ri-sticky-note-line">
        <div className="flex flex-col gap-3 sm:flex-row">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Ej. Pendiente verificar documentación. / Contactado por teléfono."
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={addingNote || !draft.trim()}
            className="inline-flex h-fit cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
          >
            <i
              className={`${addingNote ? "ri-loader-4-line animate-spin" : "ri-add-line"} text-base leading-none`}
              aria-hidden="true"
            ></i>
            Agregar
          </button>
        </div>

        <ul className="mt-4 space-y-3">
          {notas.length === 0 ? (
            <EmptyLine>Todavía no hay notas privadas.</EmptyLine>
          ) : (
            notas.map((nota) => (
              <li key={nota.id} className="rounded-md border border-background-200 bg-background-100 p-3.5">
                <p className="text-sm leading-relaxed text-foreground-800">{nota.nota}</p>
                <p className="mt-1.5 text-xs text-foreground-500">
                  {nota.autor ?? "Administrador"} · {formatFechaHora(nota.creado_en)}
                </p>
              </li>
            ))
          )}
        </ul>
      </DetailSection>

      <DetailSection title="Historial" icon="ri-history-line">
        {historial.length === 0 ? (
          <EmptyLine>Sin cambios registrados.</EmptyLine>
        ) : (
          <ol className="relative space-y-4 border-l border-background-200 pl-5">
            {historial.map((item) => {
              const meta = getAfiliadoEstadoMeta(item.estado);
              return (
                <li key={item.id} className="relative">
                  <span
                    className={`absolute -left-[26px] top-1 h-3.5 w-3.5 rounded-full border-2 border-background-50 ${meta.dot}`}
                    aria-hidden="true"
                  ></span>
                  <p className="text-sm font-label font-semibold text-foreground-900">{meta.label}</p>
                  {item.nota && <p className="mt-0.5 text-sm text-foreground-600">{item.nota}</p>}
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {formatFechaHora(item.creado_en)} · {item.autor ?? "sistema"}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </DetailSection>
    </div>
  );
}